import type { components } from '@/generated/internal-api/openapi-types';
import type { FilterOption } from '@/components/UI/Table/TableComponent.model.ts';
import { FilterTypes as filterType } from '@/constants/FilterTypes.ts';

export type WebhookApiId = components['schemas']['WebhookApiId.schema'];
export type WebhookEventV1 = components['schemas']['WebhookEventV1.schema'];
export type WebhookEventStatus = components['schemas']['WebhookEventStatus.schema'];
export type WebhookEventFilter = components['schemas']['WebhookEventFilter.schema'];

export const webhookEventStatusToStringMap: Record<WebhookEventStatus, string> = {
  completed: 'Completed',
  in_progress: 'In Progress',
  failed: 'Failed',
} as const;

const statusToFilterOptions = (): Array<{ value: string; name: string }> => {
  return Object.entries(webhookEventStatusToStringMap).map(([key, value]) => ({
    value: key,
    name: value,
  }));
};

export const webhookEventFilterOptions: Array<FilterOption> = [
  {
    value: 'status' satisfies WebhookEventFilter,
    name: 'Status',
    operations: [
      {
        name: filterType.eq,
        value: filterType.eq,
        options: statusToFilterOptions(),
      },
      {
        name: filterType.ne,
        value: filterType.ne,
        options: statusToFilterOptions(),
      },
    ],
  },
  {
    value: 'created_at' satisfies WebhookEventFilter,
    name: 'Created at',
    operations: [
      {
        name: filterType.between,
        value: filterType.between,
      },
    ],
  },
  {
    value: 'delivered_at' satisfies WebhookEventFilter,
    name: 'Delivered at',
    operations: [
      {
        name: filterType.between,
        value: filterType.between,
      },
    ],
  },
] as const;

/**
 * Automatically adds null exclusion filters when using date range filtering
 * on delivered_at or created_at, matching the pattern used in backfill/sync tables.
 */
export const webhookEventCustomFilterProcessor = (filters: Array<any>, params: URLSearchParams): void => {
  const dateFields = ['delivered_at', 'created_at'] as const;
  dateFields.forEach((field) => {
    const hasDateFilter =
      filters.some((filter) => filter.category?.value === field && filter.operation?.name === filterType.between) ??
      false;
    if (hasDateFilter) {
      params.append('filters[]', `${field}|ne|null`);
    }
  });
};
